import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { Loader2, ChevronRight, ChevronLeft, Check, KeyRound, Zap } from "lucide-react";
import { toast } from "sonner";
import SecurityReminder from "@/components/SecurityReminder";
import BookingCalendar from "@/components/BookingCalendar";

const STEPS = ["Service", "Availability", "Property & address", "Confirm"];
const EMPTY_ACCESS = { key_safe_location: "", lockbox_code: "", smart_lock_instructions: "", alarm_instructions: "", entry_notes: "" };
const EMPTY_TURNOVER = { linen_change: true, towels_replace: true, restock_consumables: false, same_day_turnaround: false, checkin_deadline: "15:00", cleaning_instructions: "", estimated_duration_hours: null };

export default function Booking() {
  const { cleanerId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const quoteId = params.get("quote") || "";
  const [cleaner, setCleaner] = useState(null);
  const [services, setServices] = useState([]);
  const [pricingOptions, setPricingOptions] = useState({});
  const [selection, setSelection] = useState({ extras: [], bedrooms: 1, bathrooms: 1, rooms: 1, first_clean: false });
  const [askQuote, setAskQuote] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ details: "", preferred_date: "" });
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState(params.get("property") || "");
  const [turnover, setTurnover] = useState(EMPTY_TURNOVER);
  const [access, setAccess] = useState(EMPTY_ACCESS);
  const [bookingType, setBookingType] = useState("standard");
  const [fees, setFees] = useState({ service_fee: 1.99, priority_fee: 4.99, admin_fee_label: "Admin Fee" });
  const [quote, setQuote] = useState(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service_slug: params.get("service") || "",
    date: "",
    start_time: "",
    duration_hours: 2,
    address_line1: "", address_city: "", address_postcode: "", special_instructions: "",
  });

  useEffect(() => {
    api.get(`/cleaners/${cleanerId}`).then((r) => {
      setCleaner(r.data);
      setForm((f) => ({ ...f, duration_hours: r.data.min_booking_hours || 2 }));
      // Skip ahead to the availability calendar when the service came from search
      const hasService = params.get("service") && r.data.services?.includes(params.get("service"));
      if (hasService && params.get("quote")) setStep(1);
    });
    api.get("/services").then((r) => setServices(r.data.services));
    api.get(`/cleaners/${cleanerId}/pricing`).then((r) => setPricingOptions(r.data.services || {})).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data.properties)).catch(() => setProperties([]));
    api.get("/fees").then((r) => setFees(r.data)).catch(() => {});
  }, [cleanerId, params]);

  const opt = pricingOptions[form.service_slug] || null;
  const isHourly = ["hourly", "hourly_or_quote", "instant_or_quote"].includes(opt?.model);
  const quoteOnly = !!opt?.quote_only && !quoteId;

  // Authoritative price breakdown, recalculated server-side on every change.
  useEffect(() => {
    if (!cleanerId) return;
    if (!quoteId && (!form.service_slug || quoteOnly)) { setQuote(null); return; }
    api.get("/bookings/quote", {
      params: {
        cleaner_id: cleanerId, duration_hours: form.duration_hours, booking_type: bookingType,
        ...(quoteId ? { quote_id: quoteId } : { service_slug: form.service_slug, selection: JSON.stringify(selection) }),
      },
    })
      .then((r) => {
        setQuote(r.data);
        if (!isHourly && r.data.duration_hours && r.data.duration_hours !== form.duration_hours) {
          setForm((f) => ({ ...f, duration_hours: r.data.duration_hours, start_time: "" }));
        }
      })
      .catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanerId, form.duration_hours, bookingType, form.service_slug, selection, quoteId]);

  // Default the duration to this service's minimum / typical time on site.
  useEffect(() => {
    if (!opt) return;
    const want = isHourly ? (opt.min_hours || 1) : (opt.est_hours || 3);
    setForm((f) => (f.duration_hours === want ? f : { ...f, duration_hours: want, start_time: "" }));
    setSelection((s) => ({ ...s, choice: opt.choices?.[0]?.key, extras: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.service_slug, pricingOptions]);

  // Selecting a saved property pre-fills the address, duration and access details.
  const selectProperty = (p) => {
    if (!p) {
      setPropertyId("");
      setAccess(EMPTY_ACCESS);
      return;
    }
    setPropertyId(p.property_id);
    setForm((f) => ({
      ...f,
      address_line1: p.address?.line1 || "", address_city: p.address?.city || "",
      address_postcode: p.address?.postcode || "",
      duration_hours: p.estimated_duration_hours || f.duration_hours,
    }));
    setAccess({ ...EMPTY_ACCESS, ...(p.access || {}) });
    setTurnover((t) => ({ ...t, estimated_duration_hours: p.estimated_duration_hours || null }));
  };

  useEffect(() => {
    if (propertyId && properties.length) {
      const p = properties.find((x) => x.property_id === propertyId);
      if (p && form.address_postcode !== p.address?.postcode) selectProperty(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, propertyId]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!cleaner) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;

  const offered = services.filter((s) => cleaner.services?.includes(s.slug));
  const isPriority = bookingType === "priority";
  // All money comes from the backend quote — the frontend never calculates the authoritative total.
  const rate = quote?.hourly_rate ?? (isPriority ? (cleaner.priority_hourly_rate || cleaner.hourly_rate) : cleaner.hourly_rate);
  const subtotal = quote?.service_amount ?? 0;
  const fee = quote?.service_fee ?? 0;
  const priorityFee = quote?.priority_fee ?? 0;
  const adminFee = quote?.admin_fee ?? 0;
  const total = quote?.customer_total ?? 0;
  const feeStandard = quote?.service_fee_standard ?? fees.service_fee ?? 1.99;
  const waivedReason = quote?.service_fee_waived_reason || null;
  const offer = quote?.offer || null;

  const canNext = () => {
    if (step === 0) return !!form.service_slug && !quoteOnly && !!quote;
    if (step === 1) return !!form.date && !!form.start_time && form.duration_hours >= 1 && form.duration_hours <= 6;
    if (step === 2) return form.address_line1 && form.address_city && form.address_postcode;
    return true;
  };

  const sendQuoteRequest = async () => {
    try {
      await api.post("/quotes", {
        cleaner_id: cleanerId, service_slug: form.service_slug,
        details: quoteForm.details, preferred_date: quoteForm.preferred_date || null,
        address_city: form.address_city || null,
      });
      toast.success("Quote request sent — you'll be notified when they reply");
      navigate("/customer?tab=quotes");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        cleaner_id: cleanerId, ...form,
        booking_type: bookingType,
        pricing_selection: quoteId ? null : selection,
        quote_id: quoteId || null,
        property_id: propertyId || null,
        turnover: propertyId ? turnover : null,
        access: propertyId ? access : null,
      });
      const { data: checkout } = await api.post("/payments/booking-checkout", {
        booking_id: booking.booking_id, origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[1fr_320px] gap-8">
        <div>
          {/* Stepper */}
          <div className="flex items-center gap-2 mb-8">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`flex items-center gap-2 ${i <= step ? "text-accent" : "text-muted-foreground"}`}>
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold ${i < step ? "bg-accent text-white" : i === step ? "bg-accent/10 text-accent border-2 border-accent" : "bg-secondary"}`}>
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className="hidden sm:inline text-sm font-medium">{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 ${i < step ? "bg-accent" : "bg-border"}`} />}
              </div>
            ))}
          </div>

          <div className="bg-card rounded-2xl border border-primary/5 p-6 sm:p-8">
            {step === 0 && (
              <div data-testid="step-service">
                <h2 className="font-display text-xl font-semibold mb-4">Which service do you need?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {offered.map((s) => {
                    const o = pricingOptions[s.slug];
                    const hourly = ["hourly", "hourly_or_quote", "instant_or_quote"].includes(o?.model);
                    const from = hourly ? (o?.hourly_rate ? `£${o.hourly_rate}/hour` : "Quote only")
                      : o?.choices?.length ? `from £${Math.min(...o.choices.map((c) => c.price)).toFixed(2)}`
                        : o?.per_room ? `£${o.per_room}/room` : "Quote only";
                    return (
                      <button key={s.slug} onClick={() => upd("service_slug", s.slug)} className={`text-left p-4 rounded-xl border-2 transition-colors ${form.service_slug === s.slug ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`} data-testid={`book-service-${s.slug}`}>
                        <p className="font-medium">{s.name}</p>
                        <p className="text-sm text-muted-foreground">{s.description}</p>
                        <p className="text-sm font-medium mt-1" data-testid={`book-service-price-${s.slug}`}>{from}</p>
                      </button>
                    );
                  })}
                </div>

                {opt && !isHourly && !quoteOnly && (
                  <div className="mt-6 space-y-4" data-testid="pricing-selection">
                    {opt.model === "property" ? (
                      <>
                        <div>
                          <label className="text-sm font-medium">How many bedrooms?</label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {opt.choices.map((c) => (
                              <button key={c.key} onClick={() => setSelection((s) => ({ ...s, bedrooms: c.bedrooms }))}
                                className={`px-4 py-2.5 rounded-xl border-2 text-sm transition-colors ${selection.bedrooms === c.bedrooms ? "border-accent bg-accent/5 font-medium" : "border-border hover:border-accent/40"}`}
                                data-testid={`sel-size-${c.key}`}>{c.label} · £{c.price.toFixed(2)}</button>
                            ))}
                          </div>
                        </div>
                        <label className="text-sm font-medium block">Bathrooms
                          <input type="number" min="1" max="6" value={selection.bathrooms}
                            onChange={(e) => setSelection((s) => ({ ...s, bathrooms: parseInt(e.target.value) || 1 }))}
                            className="bk-input" data-testid="sel-bathrooms" />
                          {opt.extra_bathroom && <span className="block text-xs text-muted-foreground mt-1">+£{opt.extra_bathroom.toFixed(2)} per additional bathroom</span>}
                        </label>
                      </>
                    ) : opt.model === "per_room" ? (
                      <label className="text-sm font-medium block">How many rooms or areas?
                        <input type="number" min="1" max="20" value={selection.rooms}
                          onChange={(e) => setSelection((s) => ({ ...s, rooms: parseInt(e.target.value) || 1 }))}
                          className="bk-input" data-testid="sel-rooms" />
                        <span className="block text-xs text-muted-foreground mt-1">
                          £{opt.per_room?.toFixed(2)} per room
                          {opt.minimum_job_value ? ` · minimum job value £${opt.minimum_job_value.toFixed(2)}` : ""}
                        </span>
                      </label>
                    ) : (
                      <div>
                        <label className="text-sm font-medium">
                          {opt.model === "turnover" ? "Property size" : opt.model === "options" ? "What needs cleaning?" : "Property type"}
                        </label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {opt.choices.map((c) => (
                            <button key={c.key} onClick={() => setSelection((s) => ({ ...s, choice: c.key }))}
                              className={`px-4 py-2.5 rounded-xl border-2 text-sm transition-colors ${selection.choice === c.key ? "border-accent bg-accent/5 font-medium" : "border-border hover:border-accent/40"}`}
                              data-testid={`sel-choice-${c.key}`}>{c.label} · £{c.price.toFixed(2)}</button>
                          ))}
                        </div>
                      </div>
                    )}

                    {opt.extras?.length > 0 && (
                      <div>
                        <label className="text-sm font-medium">Add-ons (optional)</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {opt.extras.map((e) => {
                            const on = selection.extras.includes(e.key);
                            return (
                              <button key={e.key}
                                onClick={() => setSelection((s) => ({ ...s, extras: on ? s.extras.filter((x) => x !== e.key) : [...s.extras, e.key] }))}
                                className={`px-3.5 py-2 rounded-full border-2 text-sm transition-colors ${on ? "border-accent bg-accent/10 text-accent font-medium" : "border-border hover:border-accent/40"}`}
                                data-testid={`sel-extra-${e.key}`}>
                                {on && <Check className="h-3.5 w-3.5 inline mr-1" />}{e.label} +£{e.price.toFixed(2)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {opt.model === "per_property" && opt.first_clean_surcharge && (
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={selection.first_clean}
                          onChange={(e) => setSelection((s) => ({ ...s, first_clean: e.target.checked }))}
                          data-testid="sel-first-clean" />
                        This is a first clean (+£{opt.first_clean_surcharge.toFixed(2)})
                      </label>
                    )}
                    {opt.notes?.map((n) => <p key={n} className="text-xs text-muted-foreground">{n}</p>)}
                  </div>
                )}

                {quoteOnly && (
                  <div className="mt-6 p-5 rounded-2xl border-2 border-accent/30 bg-accent/5" data-testid="quote-only-block">
                    <p className="font-display font-semibold">This job needs a quote</p>
                    <p className="text-sm mt-1">
                      {cleaner.name} prices this type of work individually.
                      {opt?.notes?.length ? ` ${opt.notes[0]}` : ""} Send the details and they'll
                      reply with a price you can accept and book in the app.
                    </p>
                    {!askQuote ? (
                      <button onClick={() => setAskQuote(true)} className="mt-4 px-6 py-3 rounded-full bg-accent text-white font-medium" data-testid="request-quote-btn">
                        Request a quote
                      </button>
                    ) : (
                      <div className="mt-4 space-y-3">
                        <textarea rows={4} value={quoteForm.details}
                          onChange={(e) => setQuoteForm({ ...quoteForm, details: e.target.value })}
                          placeholder="Describe the job — size, condition, access, anything unusual…"
                          className="bk-input" data-testid="quote-details" />
                        <input type="date" value={quoteForm.preferred_date}
                          onChange={(e) => setQuoteForm({ ...quoteForm, preferred_date: e.target.value })}
                          className="bk-input" data-testid="quote-preferred-date" />
                        <button onClick={sendQuoteRequest} className="px-6 py-3 rounded-full bg-accent text-white font-medium" data-testid="send-quote-btn">
                          Send quote request
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-datetime">
                <h2 className="font-display text-xl font-semibold mb-1">Pick a date & time</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  These are the dates {cleaner.name} has confirmed as available. Tap a green date to
                  see their times.
                </p>

                <div className="mb-4">
                  {isHourly ? (
                    <>
                      <label className="text-sm font-medium">
                        Duration: {form.duration_hours} hours (minimum {opt?.min_hours || 1}, max 6)
                      </label>
                      <input type="range" min={opt?.min_hours || 1} max="6" step="0.5" value={form.duration_hours}
                        onChange={(e) => { upd("duration_hours", parseFloat(e.target.value)); upd("start_time", ""); }}
                        className="w-full mt-2 accent-[#B46B47]" data-testid="book-duration" />
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid="book-fixed-duration">
                      This is a fixed-price job. {cleaner.name} allows about {form.duration_hours} hours
                      on site, so we'll reserve that time in their calendar.
                    </p>
                  )}
                </div>

                <BookingCalendar cleanerId={cleanerId} durationHours={form.duration_hours}
                  date={form.date} startTime={form.start_time}
                  onPick={(d, t) => setForm((f) => ({ ...f, date: d, start_time: t || "" }))} />

                {cleaner.priority_enabled && (
                  <div className="mt-6" data-testid="booking-type-picker">
                    <label className="text-sm font-medium">Booking type</label>
                    <div className="grid sm:grid-cols-2 gap-3 mt-2">
                      <button onClick={() => setBookingType("standard")}
                        className={`text-left p-4 rounded-xl border-2 transition-colors ${bookingType === "standard" ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
                        data-testid="booking-type-standard">
                        <p className="font-medium text-sm">Standard booking</p>
                        <p className="text-sm text-muted-foreground mt-0.5">£{cleaner.hourly_rate}/hour · normal turnaround</p>
                      </button>
                      <button onClick={() => setBookingType("priority")}
                        className={`text-left p-4 rounded-xl border-2 transition-colors ${bookingType === "priority" ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
                        data-testid="booking-type-priority">
                        <p className="font-medium text-sm inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-amber-500" /> Priority / urgent</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          £{cleaner.priority_hourly_rate || cleaner.hourly_rate}/hour + £{fees.priority_fee.toFixed(2)} priority fee
                        </p>
                      </button>
                    </div>
                    {bookingType === "priority" && cleaner.priority_notes && (
                      <p className="text-sm text-muted-foreground mt-2">{cleaner.priority_notes}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-address">
                <h2 className="font-display text-xl font-semibold mb-4">Where should we clean?</h2>

                {properties.length > 0 && (
                  <div className="mb-6" data-testid="property-picker">
                    <label className="text-sm font-medium">Which property?</label>
                    <div className="grid sm:grid-cols-2 gap-3 mt-2">
                      {properties.map((p) => (
                        <button key={p.property_id} onClick={() => selectProperty(p)}
                          className={`text-left p-3 rounded-xl border-2 transition-colors ${propertyId === p.property_id ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
                          data-testid={`pick-property-${p.property_id}`}>
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{p.bedrooms} bed · {p.bathrooms} bath · sleeps {p.sleeps}</p>
                        </button>
                      ))}
                      <button onClick={() => selectProperty(null)}
                        className={`text-left p-3 rounded-xl border-2 transition-colors ${!propertyId ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
                        data-testid="pick-property-none">
                        <p className="font-medium text-sm">Another address</p>
                        <p className="text-xs text-muted-foreground mt-0.5">One-off clean, not a saved property</p>
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <input value={form.address_line1} onChange={(e) => upd("address_line1", e.target.value)} placeholder="Address line 1" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-address1" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input value={form.address_city} onChange={(e) => upd("address_city", e.target.value)} placeholder="City" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-city" />
                    <input value={form.address_postcode} onChange={(e) => upd("address_postcode", e.target.value)} placeholder="Postcode" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-postcode" />
                  </div>
                  <textarea value={form.special_instructions} onChange={(e) => upd("special_instructions", e.target.value)} placeholder="Special instructions (optional)" rows={3} className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-instructions" />
                </div>

                {propertyId && (
                  <div className="mt-6 space-y-5" data-testid="turnover-options">
                    <div>
                      <h3 className="font-display font-semibold">Turnover brief</h3>
                      <p className="text-sm text-muted-foreground">Your cleaner sees this with the request so they can plan the turnaround.</p>
                      <div className="grid sm:grid-cols-2 gap-2 mt-3">
                        <Toggle label="Linen needs changing" on={turnover.linen_change} onClick={() => setTurnover((t) => ({ ...t, linen_change: !t.linen_change }))} testid="turn-linen" />
                        <Toggle label="Towels need replacing" on={turnover.towels_replace} onClick={() => setTurnover((t) => ({ ...t, towels_replace: !t.towels_replace }))} testid="turn-towels" />
                        <Toggle label="Restock consumables" on={turnover.restock_consumables} onClick={() => setTurnover((t) => ({ ...t, restock_consumables: !t.restock_consumables }))} testid="turn-restock" />
                        <Toggle label="Same-day turnaround" on={turnover.same_day_turnaround} onClick={() => setTurnover((t) => ({ ...t, same_day_turnaround: !t.same_day_turnaround }))} testid="turn-sameday" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="text-sm font-medium">Guest-ready by</label>
                          <input type="time" value={turnover.checkin_deadline || ""} onChange={(e) => setTurnover((t) => ({ ...t, checkin_deadline: e.target.value }))} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="turn-deadline" />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Estimated clean (hrs)</label>
                          <input type="number" min="1" step="0.5" value={turnover.estimated_duration_hours ?? ""} onChange={(e) => setTurnover((t) => ({ ...t, estimated_duration_hours: e.target.value === "" ? null : parseFloat(e.target.value) }))} placeholder="Optional" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="turn-estimate" />
                        </div>
                      </div>
                      <textarea rows={2} value={turnover.cleaning_instructions || ""} onChange={(e) => setTurnover((t) => ({ ...t, cleaning_instructions: e.target.value }))} placeholder="Special cleaning instructions for this turnover (optional)" className="w-full mt-4 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="turn-instructions" />
                    </div>

                    <div className="border-t border-border pt-5">
                      <h3 className="font-display font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-accent" /> Contactless access</h3>
                      <p className="text-sm text-muted-foreground">Shared only with your cleaner once they accept, and hidden again when the job is complete.</p>
                      <div className="space-y-3 mt-3">
                        <input value={access.key_safe_location} onChange={(e) => setAccess((a) => ({ ...a, key_safe_location: e.target.value }))} placeholder="Key safe location" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-access-keysafe" />
                        <input value={access.lockbox_code} onChange={(e) => setAccess((a) => ({ ...a, lockbox_code: e.target.value }))} placeholder="Lockbox code" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-access-lockbox" />
                        <input value={access.smart_lock_instructions} onChange={(e) => setAccess((a) => ({ ...a, smart_lock_instructions: e.target.value }))} placeholder="Smart lock instructions" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-access-smartlock" />
                        <input value={access.alarm_instructions} onChange={(e) => setAccess((a) => ({ ...a, alarm_instructions: e.target.value }))} placeholder="Alarm instructions" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-access-alarm" />
                        <input value={access.entry_notes} onChange={(e) => setAccess((a) => ({ ...a, entry_notes: e.target.value }))} placeholder="Entry notes" className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="book-access-entry" />
                      </div>
                      {(access.key_safe_location || access.lockbox_code) && <SecurityReminder className="mt-4" />}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-confirm">
                <h2 className="font-display text-xl font-semibold mb-4">Review your booking</h2>
                <div className="space-y-2 text-sm">
                  <Row k="Cleaner" v={cleaner.name} />
                  <Row k="Booking type" v={isPriority ? "Priority / urgent" : "Standard"} />
                  <Row k="Service" v={services.find((s) => s.slug === form.service_slug)?.name} />
                  <Row k="Date" v={form.date} />
                  <Row k="Time" v={`${form.start_time} · ${form.duration_hours}h`} />
                  <Row k="Address" v={`${form.address_line1}, ${form.address_city} ${form.address_postcode}`} />
                  {propertyId && <Row k="Property" v={properties.find((p) => p.property_id === propertyId)?.name} />}
                  {propertyId && <Row k="Turnover" v={[turnover.linen_change && "linen", turnover.towels_replace && "towels", turnover.restock_consumables && "restock", turnover.same_day_turnaround && "same-day"].filter(Boolean).join(", ") || "standard"} />}
                  {propertyId && turnover.checkin_deadline && <Row k="Guest-ready by" v={turnover.checkin_deadline} />}
                </div>
                {propertyId && (
                  <div className="mt-4 p-4 rounded-xl bg-accent/5 border border-accent/20 text-sm" data-testid="confirm-turnover-note">
                    Your turnover checklist and access details are attached. Access details unlock for {cleaner.name} only after they accept, and hide again once you approve the completed clean.
                  </div>
                )}
                <div className="mt-5 p-4 rounded-xl bg-secondary/50 text-sm" data-testid="confirm-price-note">
                  <div className="space-y-1.5">
                    {(quote?.price_lines?.length ? quote.price_lines : [{ label: `${isPriority ? "Priority rate" : "Cleaner's rate"} £${rate} × ${form.duration_hours}h`, amount: subtotal }]).map((l) => (
                      <div key={l.label} className="flex justify-between"><span>{l.label}</span><span>£{l.amount.toFixed(2)}</span></div>
                    ))}
                    <div className="flex justify-between font-medium"><span>Cleaning / service price</span><span data-testid="confirm-subtotal">£{subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>My Clean Connect™ service charge</span><ServiceFeeValue fee={fee} standard={feeStandard} reason={waivedReason} testid="confirm-service-fee" /></div>
                    {isPriority && <div className="flex justify-between"><span>Priority booking fee</span><span>£{priorityFee.toFixed(2)}</span></div>}
                    <div className="flex justify-between"><span>Admin / payment fee</span><span data-testid="confirm-admin-fee">£{adminFee.toFixed(2)}</span></div>
                    <div className="flex justify-between font-semibold pt-1.5 border-t border-border"><span>Total to pay</span><span data-testid="confirm-total">£{total.toFixed(2)}</span></div>
                  </div>
                  <p className="mt-2">Your card will be <strong>authorised</strong> now, but only charged once {cleaner.name} accepts. Your booking is not guaranteed until then.</p>
                </div>
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full border border-border font-medium disabled:opacity-40 hover:bg-secondary transition-colors" data-testid="book-back">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              {step < 3 ? (
                <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="inline-flex items-center gap-1 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-40 hover:bg-primary/90 transition-colors" data-testid="book-next">
                  Continue <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-accent text-white font-medium disabled:opacity-40 hover:bg-accent/90 transition-colors" data-testid="book-confirm">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Pay & Request
                </button>
              )}
            </div>
          </div>
        </div>

        {/* SUMMARY */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-card rounded-2xl border border-primary/5 p-6">
            <div className="flex items-center gap-3">
              <img src={cleaner.photo_url} alt="" className="h-14 w-14 rounded-xl object-cover" />
              <div>
                <p className="font-display font-semibold">{cleaner.name}</p>
                <p className="text-sm text-muted-foreground">{cleaner.town}</p>
              </div>
            </div>
            <div className="mt-5 space-y-2 text-sm" data-testid="price-breakdown">
              {isPriority && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold mb-1" data-testid="summary-priority-tag">
                  <Zap className="h-3.5 w-3.5" /> Priority booking
                </span>
              )}
              {(quote?.price_lines || []).map((l) => (
                <div key={l.label} className="flex justify-between"><span className="text-muted-foreground">{l.label}</span><span>£{l.amount.toFixed(2)}</span></div>
              ))}
              <div className="flex justify-between"><span className="text-muted-foreground">Cleaning / service price</span><span data-testid="summary-subtotal">£{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">My Clean Connect™ service charge</span><ServiceFeeValue fee={fee} standard={feeStandard} reason={waivedReason} testid="summary-service-fee" /></div>
              {isPriority && (
                <div className="flex justify-between"><span className="text-muted-foreground">Priority booking fee</span><span data-testid="summary-priority-fee">£{priorityFee.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-muted-foreground">Admin / payment fee</span><span data-testid="summary-admin-fee">£{adminFee.toFixed(2)}</span></div>
              <div className="flex justify-between font-display font-semibold text-base pt-2 border-t border-border"><span>Total to pay</span><span data-testid="summary-total">£{total.toFixed(2)}</span></div>
              {waivedReason === "new_customer_offer" && offer && (
                <p className="text-xs text-accent font-medium pt-1" data-testid="summary-offer-note">
                  {offer.headline} {offer.remaining} of {offer.total} remaining — only completed cleans count.
                </p>
              )}
              {waivedReason === "premium_cleaner" && (
                <p className="text-xs text-accent font-medium pt-1" data-testid="summary-premium-cleaner-note">
                  This cleaner is Premium, so there's no service charge — and it doesn't use up one of
                  your 5 service-charge-free cleans.
                </p>
              )}
              <p className="text-xs text-muted-foreground pt-1" data-testid="summary-fee-note">
                {fees.admin_fee_note || "The Admin Fee covers the card processing cost so nothing is taken from your cleaner or our service fee."}
                {" "}Nothing is charged until you confirm.
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>;
}

const WAIVED_LABEL = { premium_cleaner: "Premium Cleaner", new_customer_offer: "New Customer Offer" };

function ServiceFeeValue({ fee, standard, reason, testid }) {
  if (fee > 0 || !reason) return <span data-testid={testid}>£{fee.toFixed(2)}</span>;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap justify-end" data-testid={testid}>
      <span className="line-through text-muted-foreground">£{standard.toFixed(2)}</span>
      <span className="font-semibold">£0.00</span>
      <span className="px-1.5 py-0.5 rounded-full bg-accent/10 text-accent text-[11px] font-semibold whitespace-nowrap" data-testid={`${testid}-label`}>
        {WAIVED_LABEL[reason]}
      </span>
    </span>
  );
}

function Toggle({ label, on, onClick, testid }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2.5 p-3 rounded-xl border-2 text-left text-sm transition-colors ${on ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`} data-testid={testid}>
      <span className={`h-5 w-5 rounded-md flex items-center justify-center border ${on ? "bg-accent border-accent text-white" : "border-border"}`}>
        {on && <Check className="h-3.5 w-3.5" />}
      </span>
      {label}
    </button>
  );
}
